/**
 * Shared 1-to-1 email modal for super-admin outreach.
 *
 * Used by:
 *   - /dispatch/admin/at-risk-customers (apology to address-shape victims)
 *   - /dispatch/admin/failed-orders     (recovery to checkout-broken victims)
 *
 * Renders against the 7 templates in `lib/emailTemplates.js`. The caller can
 * pass `context` to pre-select the right template (`template_id`,
 * `refund_amount`, `payment_failed`, `delivery_failed`, etc.) so the default
 * dropdown selection matches the operational intent without manual clicks.
 *
 * Goes through POST /api/admin/email/send-direct → Resend → audit log.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, X } from 'lucide-react';
import { EMAIL_TEMPLATES, defaultTemplateFor, renderTemplate } from '../lib/emailTemplates';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export function SendDirectEmailModal({ customer, context = {}, title, onClose, onSent }) {
  const initial = defaultTemplateFor(customer, context);
  // Detect which template the default matched so the dropdown reflects it.
  const initialTplId =
    context.template_id ||
    EMAIL_TEMPLATES.find((t) => t.subject === initial.subject)?.id ||
    EMAIL_TEMPLATES[0].id;
  const [templateId, setTemplateId] = useState(initialTplId);
  const [subject, setSubject] = useState(initial.subject);
  const [body, setBody] = useState(initial.body_text);
  const [sending, setSending] = useState(false);

  function pickTemplate(newId) {
    setTemplateId(newId);
    const rendered = renderTemplate(newId, customer, context);
    setSubject(rendered.subject);
    setBody(rendered.body_text);
  }

  async function send() {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (!customer?.email) {
      toast.error('Customer email is missing');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/email/send-direct`,
        { to_email: customer.email, subject, body_text: body },
        { withCredentials: true },
      );
      toast.success(`Sent to ${customer.email}`);
      onSent?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (err?.message || 'Send failed');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="send-direct-email-overlay"
    >
      <div
        className="bg-[#0A0A0A] border border-[#39FF14]/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#39FF14]/20">
          <h2 className="font-mono text-sm uppercase tracking-widest text-[#39FF14]">
            // {title || 'SEND EMAIL'} → {customer?.email || '(no email)'}
          </h2>
          <button onClick={onClose} className="text-[#82937B] hover:text-white" data-testid="send-direct-email-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-[#82937B] block mb-1">
              Template
            </label>
            <select
              value={templateId}
              onChange={(e) => pickTemplate(e.target.value)}
              className="w-full bg-[#050505] border border-[#39FF14]/30 text-white px-3 py-2 font-mono text-sm focus:border-[#39FF14] focus:outline-none"
              data-testid="send-direct-email-template-picker"
            >
              {EMAIL_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#82937B]/60 mt-1">
              Picking a template replaces subject + body. Edit freely after selecting.
            </p>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-[#82937B] block mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-[#050505] border border-[#39FF14]/30 text-white px-3 py-2 font-mono text-sm focus:border-[#39FF14] focus:outline-none"
              data-testid="send-direct-email-subject"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-[#82937B] block mb-1">
              Body (plain text — branded shell + satisfaction footer added automatically)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full bg-[#050505] border border-[#39FF14]/30 text-white px-3 py-2 font-mono text-sm focus:border-[#39FF14] focus:outline-none whitespace-pre-wrap"
              data-testid="send-direct-email-body"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest border border-[#82937B]/40 text-[#82937B] hover:text-white"
              data-testid="send-direct-email-cancel"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[#39FF14] text-[#050505] hover:bg-[#39FF14]/80 disabled:opacity-50 flex items-center gap-2"
              data-testid="send-direct-email-send"
            >
              <Mail size={14} />
              {sending ? 'Sending…' : '> Send email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendDirectEmailModal;
